import Immutable from 'immutable'

import {
  createCreditCard,
  createError,
  createOfferFilter,
  createSignup,
} from 'internal-components/src/records'

import * as constants from './constants'

// ------------------------------------
// Reducer
// ------------------------------------
const meta = Immutable.Map({
  emailChecking: false,
  emailRegistered: null,
  filter: createOfferFilter(),
  partnerApproved: true,
  qualificationResults: Immutable.Map(),
  revisiting: false,
  validating: false,
  working: false,
})

export const initialState = Immutable.fromJS({
  card: createCreditCard(),
  edit: null,
  errors: Immutable.Set(),
  knownSignups: Immutable.Map(),
  meta,
})

export default function reducer(state = initialState, { payload, type }) {
  switch (type) {
    case constants.ADD_ERRORS: {
      const newErrors = payload.violations.map(v => createError(v))

      return state.set('errors', state.get('errors').union(newErrors))
    }

    case constants.AGREE_TERMS:
      return state.setIn(['edit', 'agreeTerms'], payload.agree)

    case constants.BEGIN:
      return state
        .set('edit', createSignup({}))
        .set('errors', Immutable.Set())
        .set('card', createCreditCard())
        .set('meta', meta)

    case constants.BEGIN_PARTNER:
      return state
        .set('edit', createSignup({}))
        .set('errors', Immutable.Set())
        .set('card', createCreditCard())
        .setIn(['edit', 'partner'], payload.partner)
        .set('meta', meta)

    case constants.BEGIN_VALIDATION:
      return state.setIn(['meta', 'validating'], true)

    case constants.BEGIN_WORK:
      return state.setIn(['meta', 'working'], true)

    case constants.CHECKED_EMAIL:
      return state
        .setIn(['meta', 'emailChecking'], false)
        .setIn(['meta', 'emailRegistered'], payload.registered)

    case constants.CHECKING_EMAIL:
      return state
        .setIn(['meta', 'emailChecking'], true)
        .setIn(['meta', 'emailRegistered'], null)

    case constants.CLEAR_ERRORS:
      return state.set('errors', Immutable.Set())

    case constants.END_VALIDATION:
      return state.setIn(['meta', 'validating'], false)

    case constants.END_WORK:
      return state.setIn(['meta', 'working'], false)

    case constants.FORGET_ALL_SIGNUPS:
      return state.set('knownSignups', Immutable.Map())

    case constants.FORGET_SIGNUP:
      return state.deleteIn(['knownSignups', payload.reference])

    case constants.REMEMBER_SIGNUP:
      return state.setIn(['knownSignups', payload.reference], payload.accessKey)

    case constants.REPLACE_CURRENT_SIGNUP: {
      let newMeta = meta; // Default

      if (payload.signup && state.getIn(['edit', 'addressLocId']) === payload.signup.addressLocId) {
        // Refreshing the active signup. Leave the qualification results alone.
        newMeta = newMeta.setIn(['qualificationResults'], state.getIn(['meta', 'qualificationResults']));
      }

      state = state
        .set('edit', payload.signup)
        .set('errors', Immutable.Set())
        .set('card', createCreditCard())
        .set('meta', newMeta)

      if (typeof payload.agree !== 'undefined') {
        state = state.setIn(['edit', 'agreeTerms'], payload.agree)
      }

      return state
    }

    case constants.REVISITING_SIGNUP:
      return state.setIn(['meta', 'revisiting'], true)

    case constants.SELECT_CLIENT:
      return state
        .setIn(['edit', 'clientId'], payload.clientId)
        .setIn(['edit', 'billingGroupId'], payload.billingGroupId)

    case constants.SELECT_OFFER:
      return state.setIn(['edit', 'offer'], payload.offerId)

    case constants.SELECT_TOKEN:
      return state.setIn(['edit', 'token'], payload.tokenUuid)

    case constants.SELECT_USER:
      return state.setIn(['edit', 'user'], payload.userId)

    case constants.SET_ABN:
      return state.setIn(['edit', 'abn'], payload.abn)

    case constants.SET_ADDRESS:
      return state
        .setIn(['edit', 'address'], payload.address)
        .setIn(['meta', 'filter', 'addressSupplied'], payload.address !== '')

    case constants.SET_ADDRESS_LOCID:
      return state.setIn(['edit', 'addressLocId'], payload.locId)

    case constants.SET_CARD_NAME:
      return state.setIn(['card', 'name'], payload.cardName)

    case constants.SET_CARD_NUMBER:
      return state.setIn(['card', 'number'], payload.cardNumber)

    case constants.SET_CHALLENGE_PHRASE:
      return state.setIn(['edit', 'challengePhrase'], payload.challengePhrase)

    case constants.SET_COMPANY_NAME:
      return state.setIn(['edit', 'companyName'], payload.companyName)

    case constants.SET_COMPANY_SIZE:
      return state.setIn(['edit', 'companySize'], payload.companySize)

    case constants.SET_CONTACT:
      return state.setIn(['edit', 'contact'], payload.contact)

    case constants.SET_COUPON:
      return state
        .setIn(['edit', 'coupon'], payload.coupon)
        .setIn(['edit', 'hasCoupon'], false)

    case constants.SET_CUSTOMER_REFERENCE:
      return state.setIn(['edit', 'customerReference'], payload.reference)

    case constants.SET_CVN:
      return state.setIn(['card', 'cvn'], payload.cvn)

    case constants.SET_DATA:
      if (payload.value === undefined) {
        return state.deleteIn(['edit', 'data', payload.key])
      } else {
        return state.setIn(['edit', 'data', payload.key], payload.value)
      }

    case constants.SET_DATE_OF_BIRTH:
      return state.setIn(['edit', 'dateOfBirth'], payload.dateOfBirth)

    case constants.SET_EMAIL:
      return state.setIn(['edit', 'email'], payload.email)

    case constants.SET_EMAIL_CONFIRM:
      return state.setIn(['edit', 'emailConfirm'], payload.email)

    case constants.SET_EXPIRY_MONTH:
      return state.setIn(['card', 'expiry_month'], payload.expiryMonth)

    case constants.SET_EXPIRY_YEAR:
      return state.setIn(['card', 'expiry_year'], payload.expiryYear)

    case constants.SET_EXTRA_OFFER_ID:
      return state.setIn(['edit', 'extraOffer'], payload.extraOfferId)

    case constants.SET_FIRST_NAME:
      return state.setIn(['edit', 'firstName'], payload.firstName)

    case constants.SET_LAST_NAME:
      return state.setIn(['edit', 'lastName'], payload.lastName)

    case constants.SET_NEEDS_VOICE:
      return state.setIn(['meta', 'needsVoice'], payload.needsVoice)

    case constants.SET_PARTNER_APPROVED:
      return state.setIn(['meta', 'partnerApproved'], payload.approved)

    case constants.SET_PREFERRED_APPOINTMENT_DATE:
      return state.setIn(['edit', 'preferredAppointmentDate'], payload.preferredAppointmentDate)

    case constants.SET_PREFERRED_APPOINTMENT_TIME:
      return state.setIn(['edit', 'preferredAppointmentTime'], payload.preferredAppointmentTime)

    case constants.SET_QUALIFICATION:
      return state.setIn(['edit', 'qualification'], payload.qualification)

    case constants.SET_QUALIFICATION_RESULT: {
      const results = Object.keys(payload.annotatedResults).reduce(
        (acc, index) =>
          acc.set(parseInt(index, 10), payload.annotatedResults[index]),
        Immutable.Map(),
      )

      return state.setIn(['meta', 'qualificationResults'], results)
    }

    case constants.SET_SITE_CONTACT_FIRST_NAME:
      return state.setIn(['edit', 'siteContactFirstName'], payload.contactFirstName)

    case constants.SET_SITE_CONTACT_LAST_NAME:
      return state.setIn(['edit', 'siteContactLastName'], payload.contactLastName)

    case constants.SET_SITE_CONTACT_NUMBER:
      return state.setIn(['edit', 'siteContactNumber'], payload.contactNumber)

    case constants.SET_SERVICE_PASSWORD:
      return state
        .setIn(['edit', 'servicePassword'], payload.servicePassword)
        .setIn(['edit', 'hasServicePassword'], false)

    case constants.SET_USE_EXISTING_NUMBER:
      return state.setIn(['meta', 'useExistingNumber'], payload.useExistingNumber)

    case constants.SET_USER_PASSWORD:
      return state
        .setIn(['edit', 'userPassword'], payload.userPassword)
        .setIn(['edit', 'hasUserPassword'], false)

    case constants.UPDATE_FILTER:
      return state.setIn(['meta', 'filter', payload.field], payload.value)

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/redux/modules/signupEdit/reducer.js